<template>
  <div>
    <!-- Overlay -->
    <transition name="fade">
      <div
        v-if="isOpen"
        class="drawer-overlay"
        @click="close"
      ></div>
    </transition>

    <!-- Drawer panel -->
    <transition name="slide">
      <div v-if="isOpen" class="drawer" role="dialog" aria-modal="true">

        <!-- Header -->
        <div class="drawer-header">
          <div class="drawer-meta" v-if="paper">
            <span class="drawer-eyebrow">
              {{ categoryOptions[paper.category] || 'Publication' }}
              <span v-if="paper.year"> · {{ paper.year }}</span>
            </span>
          </div>
          <button type="button" class="drawer-close" @click="close" aria-label="Close">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Loading state -->
        <div class="drawer-loading" v-if="$apollo.loading">
          <div class="spinner"></div>
          <span>Loading paper…</span>
        </div>

        <!-- Content -->
        <div class="drawer-body" v-else-if="paper">

          <h2 class="drawer-title">{{ paper.title }}</h2>

          <p class="drawer-authors" v-if="paper.authors && paper.authors.length">
            {{ paper.authors.map(a => a.name).join(', ') }}
          </p>

          <!-- Action buttons -->
          <div class="drawer-actions">
            <a
              v-if="paper.document"
              @click="redirectToPDF(paper)"
              class="btn-primary"
            >
              <i class="fa fa-file-pdf-o"></i> View PDF
            </a>
            <a
              v-if="paper.url"
              @click="redirectToUrl(paper)"
              class="btn-secondary"
            >
              <i class="fa fa-external-link"></i> Visit URL
            </a>
          </div>

          <div class="drawer-divider"></div>

          <!-- Metadata grid -->
          <dl class="meta-grid">

            <div class="meta-row" v-if="paper.mainTopics && paper.mainTopics.length">
              <dt>Topics</dt>
              <dd>
                <span
                  class="badge badge-topic"
                  v-for="topic in paper.mainTopics"
                  :key="topic"
                >
                  {{ topicOptions[topic] }}
                </span>
              </dd>
            </div>

            <div class="meta-row" v-if="paper.category">
              <dt>Category</dt>
              <dd>
                <span class="badge badge-category">{{ categoryOptions[paper.category] }}</span>
              </dd>
            </div>

            <div class="meta-row" v-if="paper.region">
              <dt>Region</dt>
              <dd>{{ regionOptions[paper.region] }}</dd>
            </div>

            <div class="meta-row" v-if="paper.language">
              <dt>Language</dt>
              <dd>
                <span class="badge badge-lang">{{ languageOptions[paper.language] }}</span>
              </dd>
            </div>

            <div class="meta-row" v-if="paper.url">
              <dt>URL</dt>
              <dd>
                <a :href="paper.url" target="_blank" rel="noopener noreferrer">{{ paper.url }}</a>
              </dd>
            </div>

            <div class="meta-row" v-if="paper.sdgs && paper.sdgs.length">
              <dt>SDGs</dt>
              <dd>
                <span
                  class="badge badge-sdg"
                  v-for="sdg in getSDGs(paper)"
                  :key="sdg"
                >
                  {{ sdg }}
                </span>
              </dd>
            </div>

            <div class="meta-row" v-if="paper.tags && paper.tags.length">
              <dt>Tags</dt>
              <dd>
                <span
                  class="badge badge-tag"
                  v-for="tag in paper.tags"
                  :key="tag.name"
                >
                  {{ topicOptions[tag.name] || tag.name }}
                </span>
              </dd>
            </div>

            <div class="meta-row">
              <dt>Available online</dt>
              <dd>
                <span :class="['badge', paper.available ? 'badge-yes' : 'badge-no']">
                  {{ paper.available ? 'Yes' : 'No' }}
                </span>
              </dd>
            </div>

          </dl>

        </div>

        <!-- Error/Empty state -->
        <div class="drawer-body drawer-empty" v-else>
          <p>Unable to load paper details. Please try again.</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { PAPER_DETAILS_QUERY } from '@/graphql'
import { topicOptions, categoryOptions, languageOptions, regionOptions, sdgOptions } from '@/options'

export default {
  name: 'PaperDrawer',
  props: {
    paperId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      paper: undefined,
      topicOptions,
      categoryOptions,
      languageOptions,
      regionOptions,
      sdgOptions
    }
  },
  computed: {
    isOpen () {
      return this.paperId !== null
    }
  },
  watch: {
    paperId (id) {
      if (id !== null) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
        this.paper = null
      }
    }
  },
  apollo: {
    paper: {
      query: PAPER_DETAILS_QUERY,
      variables () {
        return { id: parseInt(this.paperId) }
      },
      skip () {
        return this.paperId === null
      },
      error (errorDetails) {
        if (
          errorDetails.message.includes('Authentication Failure') ||
          errorDetails.message.includes('Signature has expired')
        ) {
          localStorage.removeItem('app-token')
        }
      }
    }
  },
  methods: {
    close () {
      this.$emit('close')
    },
    redirectToUrl (paper) {
      window.location.href = paper.url
    },
    redirectToPDF (paper) {
      const MINIO_STORAGE_ENDPOINT = 'https://docs-s3.infra.hedera.online'
      const MINIO_STORAGE_MEDIA_BUCKET_NAME = 'online-papers-library'
      window.open(`${MINIO_STORAGE_ENDPOINT}/${MINIO_STORAGE_MEDIA_BUCKET_NAME}/${paper.document}`, '_blank')
    },
    getSDGs (paper) {
      const names = []
      ;(paper.sdgs || []).forEach(element => {
        const match = sdgOptions.find(e => e.value === element)
        if (match) names.push(match.name)
      })
      return names
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

/* ── Overlay ── */
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 400;
}

/* ── Drawer panel ── */
.drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 480px;
  max-width: 100vw;
  height: 100vh;
  background: #ffffff;
  z-index: 500;
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', sans-serif;
  overflow: hidden;
}

/* ── Header ── */
.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eaf2ec;
  flex-shrink: 0;
}

.drawer-eyebrow {
  font-size: 11px;
  font-weight: 600;
  color: #7aaa8d;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.drawer-close {
  width: 32px;
  height: 32px;
  border: 1px solid #d5e8db;
  border-radius: 6px;
  background: #fff;
  color: #5a6e62;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: background 0.15s, color 0.15s;
}

.drawer-close:hover {
  background: #1b3d2f;
  color: #fff;
  border-color: #1b3d2f;
}

/* ── Body ── */
.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 24px 40px;
}

/* ── Loading ── */
.drawer-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: #7aaa8d;
  font-size: 13px;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #eaf2ec;
  border-top-color: #2d6a4f;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Title & authors ── */
.drawer-title {
  font-size: 18px;
  font-weight: 700;
  color: #1b3d2f;
  line-height: 1.35;
  margin-bottom: 8px;
}

.drawer-authors {
  font-size: 13px;
  color: #5a6e62;
  font-style: italic;
  margin-bottom: 20px;
}

/* ── Action buttons ── */
.drawer-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 18px;
  background: #1b3d2f;
  color: #ffffff;
  border-radius: 7px;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: none;
  transition: background 0.15s;
}

.btn-primary:hover {
  background: #2d6a4f;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 18px;
  background: #ffffff;
  color: #2c5542;
  border: 1px solid #c8dbd3;
  border-radius: 7px;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-secondary:hover {
  background: #f0f7f2;
}

/* ── Divider ── */
.drawer-divider {
  height: 1px;
  background: #eaf2ec;
  margin: 20px 0;
}

/* ── Metadata grid ── */
.meta-grid {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.meta-row {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 10px;
  align-items: start;
}

.meta-row dt {
  font-size: 11px;
  font-weight: 700;
  color: #7aaa8d;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding-top: 3px;
}

.meta-row dd {
  font-size: 13px;
  color: #2c3e35;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0;
}

/* ── Badges ── */
.badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-category { background: #eaf3de; color: #3b6d11; }
.badge-topic    { background: #f0f9f4; color: #2d6a4f; border: 1px solid #c8dbd3; }
.badge-lang     { background: #e6f1fb; color: #185fa5; }
.badge-sdg      { background: #faeeda; color: #633806; }
.badge-tag      { background: #f1efe8; color: #5f5e5a; }
.badge-yes      { background: #eaf3de; color: #3b6d11; }
.badge-no       { background: #fcebeb; color: #a32d2d; }

/* ── Permalink ── */
.permalink {
  font-size: 12px;
  color: #7aaa8d;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 0.15s;
}

.permalink:hover {
  color: #2d6a4f;
}

/* ── Empty state ── */
.drawer-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7aaa8d;
  font-size: 14px;
  text-align: center;
}

/* ── Transitions ── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
}

/* ── Mobile ── */
@media (max-width: 768px) {
  .drawer {
    width: 100vw;
    bottom: 0;
    max-height: 100vh;
    left: auto;
  }

  .drawer-body {
    padding-bottom: env(safe-area-inset-bottom, 24px);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Papers/PaperDrawer.vue